// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductActions, { ProductActionsStyle } from "@conklin/App/Components/Product/ProductActions";
import { xIsAuthenticated } from "@conklin/App/Store/Selectors/GeneralSelectors";
import { ButtonPresentationProps } from "@conklin/MobiusOverrides/Button/Button";
import ProductAddToCartButton from "@conklin/Overrides/Components/ProductAddToCartButton";
import { parseBoolean } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductQuantityBreakPricingStyles } from "@insite/content-library/Components/ProductQuantityBreakPricing";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    rfqRequestQuotePageUrl: getPageLinkByPageType(state, "RfqRequestQuotePage")?.url,
    isAuthenticated: xIsAuthenticated(state),
    configurationCompleted: state.pages.productDetails.configurationCompleted,
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
});

export interface ProductDetailsProductInfoStyles {
    wrapper?: InjectableCss;
    separator?: InjectableCss;
    breakPricingLink?: LinkPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    requestQuote?: ButtonPresentationProps;
    productActionsStyle?: ProductActionsStyle;
    multiplesOf?: TypographyPresentationProps;
}

export const productDetailsProductInfoStyles: ProductDetailsProductInfoStyles = {};

const styles = productDetailsProductInfoStyles;

const ProductDetailsProductInfo: React.FC<Props> = ({
    product,
    productInfo,
    isAuthenticated,
    configurationCompleted,
    variantSelectionCompleted,
}) => {
    if (!product) {
        return null;
    }

    const multipleSaleQty = product.detail?.multipleSaleQty || 0;
    const invalidPrice =
        (!product.allowZeroPricing && productInfo.pricing && getUnitNetPrice(productInfo.pricing, 1).price === 0) ||
        productInfo.failedToLoadPricing;

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="product-actions-grid-template">
            {createWidgetElement("ProductDetails/ProductDetailsPrice", {
                fields: {},
            })}
            {createWidgetElement("ProductDetails/ProductDetailsAvailability", {
                fields: {},
            })}

            {multipleSaleQty > 1 && (
                <Typography {...styles.multiplesOf}>
                    {translate("Multiples of ")}
                    {multipleSaleQty}
                </Typography>
            )}

            <ProductActions
                showAddToList={false}
                isPdp={true}
                showQty={true}
                extendedStyles={styles.productActionsStyle}
            />

            {createWidgetElement("ProductDetails/ProductDetailsAddToListLink", {
                fields: {},
            })}

            {!product.quoteRequired &&
                !(!isAuthenticated && parseBoolean(product.properties.authRequiredForPurchase)) &&
                (!product.isVariantParent || (product.isVariantParent && variantSelectionCompleted)) && (
                    <>
                        <StyledWrapper {...styles.separator} />
                        <ProductAddToCartButton
                            extendedStyles={styles.requestQuote}
                            data-test-selector="secondaryRequestQuoteBtn"
                            secondaryRequestQuoteBtn={true}
                            {...{ isPdp: true, configurationCompleted, variantSelectionCompleted }}
                        />
                    </>
                )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(ProductDetailsProductInfo)),
    definition: {
        displayName: "Product Info",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
